import { formatDuration, intervalToDuration } from 'date-fns';

import { notUndefinedOrNull } from 'utils';

import { USDLProductConfig } from './types';

const usdlFiledDict = {
  DCT: 'firstNameAndMiddleName',
  DCA: 'jurisdictionVehicleClass',
  DCB: 'jurisdictionRestrictionCodes',
  DCD: 'jurisdictionEndorsementCodes',
  DBA: 'dateOfExpiry',
  DCS: 'lastName',
  DAC: 'firstName',
  DAD: 'middleName',
  DBD: 'dateOfIssue',
  DBB: 'dateOfBirth',
  DBC: 'sex',
  DAY: 'eyeColor',
  DAU: 'height',
  DAG: 'addressStreet',
  DAI: 'addressCity',
  DAJ: 'addressState',
  DAK: 'addressPostalCode',
  DAQ: 'documentNumber',
  DCF: 'documentDiscriminator',
  DCG: 'issuer',
  DDE: 'lastNameTruncated',
  DDF: 'firstNameTruncated',
  DDG: 'middleNameTruncated',
  // optional
  DAZ: 'hairColor',
  DAH: 'addressStreet2',
  DCI: 'placeOfBirth',
  DCJ: 'auditInformation',
  DCK: 'inventoryControlNumber',
  DBN: 'otherLastName',
  DBG: 'otherFirstName',
  DBS: 'otherSuffixName',
  DCU: 'nameSuffix', // e.g. jr, sr
  DCE: 'weightRange',
  DCL: 'race',
  DCM: 'standardVehicleClassification',
  DCN: 'standardEndorsementCode',
  DCO: 'standardRestrictionCode',
  DCP: 'jurisdictionVehicleClassificationDescription',
  DCQ: 'jurisdictionEndorsementCodeDescription',
  DCR: 'jurisdictionRestrictionCodeDescription',
  DDA: 'complianceType',
  DDB: 'dateCardRevised',
  DDC: 'dateOfExpiryHazmatEndorsement',
  DDD: 'limitedDurationDocumentIndicator',
  DAW: 'weightLb',
  DAX: 'weightKg',
  DDH: 'dateAge18',
  DDI: 'dateAge19',
  DDJ: 'dateAge21',
  DDK: 'organDonor',
  DDL: 'veteran',
};

export const addLineBreaksToCode = (codeProp: string): string => {
  if (!codeProp) return '';
  return Object.keys(usdlFiledDict).reduce((code, key) => {
    const keyIndex = code.indexOf(key);
    if (keyIndex === -1) return code;
    return `${code.slice(0, keyIndex)}\n${code.slice(keyIndex)}`;
  }, codeProp);
};

/**
 * Extracts information from USDL
 * Based on DL/ID Standards - https://www.aamva.org/DL-ID-Card-Design-Standard/
 */
export const extractInfo = (code: string): Record<string, string> => {
  if (!code) return {};
  const pairs = code
    .split(/\n/)
    .map(row => {
      const c = row.substring(0, 3);
      if (usdlFiledDict[c]) {
        return [usdlFiledDict[c], row.substring(3)] as [string, string];
      }
      return undefined;
    })
    .filter(notUndefinedOrNull);

  return Object.fromEntries(pairs);
};

export const getAge = (dateString: string): number => {
  if (!dateString) return 0;
  const today = new Date();
  const month = Number(dateString.slice(0, 2)) - 1;
  const date = Number(dateString.slice(2, 4));
  const year = Number(dateString.slice(-4));
  const age = today.getFullYear() - year;
  const m = today.getMonth() - month;

  if (m < 0 || (m === 0 && today.getDate() < date)) {
    return age - 1;
  }
  return age;
};

export const getTimeTillExpiry = (dateString: string): string => {
  if (!dateString) return '';
  const duration = intervalToDuration({
    start: new Date(
      Number(dateString.slice(-4)),
      Number(dateString.slice(0, 2)) - 1,
    ),
    end: new Date(),
  });

  return formatDuration(duration, {
    format: ['years', 'months', 'days'],
    delimiter: ', ',
  });
};

/**
 * Combines USDL product and product group configurations
 *
 * Returns USDLProductConfig applying the highest minAge and lowest positive maxAmount
 */
export const combineConfiguration = (
  productConfiguration: USDLProductConfig | undefined,
  productGroupConfiguration: USDLProductConfig | undefined,
): USDLProductConfig => ({
  maxAmount:
    productConfiguration?.maxAmount && productGroupConfiguration?.maxAmount
      ? Math.min(
          productConfiguration.maxAmount,
          productGroupConfiguration.maxAmount,
        )
      : (productConfiguration?.maxAmount ||
          productGroupConfiguration?.maxAmount) ??
        0,
  minAge: Math.max(
    productConfiguration?.minAge ?? 0,
    productGroupConfiguration?.minAge ?? 0,
  ),
});
